// -----------------------------------------------------------------------------
//  BUILDING A CSV THAT SURVIVES CONTACT WITH A SPREADSHEET
// -----------------------------------------------------------------------------
//
//  A CSV looks like the simplest file format in the world, which is exactly why
//  it is usually got wrong. Three specific things go wrong with THIS data, and
//  all three are silent — the file opens, it looks fine, and it is wrong.
//
//  1. KHMER TURNS INTO MOJIBAKE.
//     Excel does not assume UTF-8. Opened without a byte order mark, "សុខ បុប្ផា"
//     arrives as a row of question marks or Latin gibberish. The fix is three
//     bytes at the front of the file (U+FEFF). Google Sheets ignores the mark
//     and reads UTF-8 correctly either way, so the mark costs nothing and
//     rescues every Excel user.
//
//  2. A NAME BECOMES A FORMULA.
//     A cell whose text starts with = + - @ (or a tab, or a carriage return) is
//     executed by Excel and Sheets as a formula. A household called "=Sok" is
//     unlikely; a phone number typed as "+855 12 345 678" is not, and it will
//     silently become the number 85512345678 — or, in the nastier case, a
//     crafted value becomes a live formula in someone else's spreadsheet. This
//     is a real injection class, not a curiosity: it is how a data-collection
//     system turns into a way of running code on a manager's laptop.
//
//     The guard is a single leading apostrophe, which spreadsheets read as
//     "this is text" and do not display. It is applied ONLY to text values, so
//     a genuine negative number in a numeric column stays a number.
//
//  3. COMMAS AND NEWLINES INSIDE THE DATA.
//     A status note containing a comma silently shifts every column after it by
//     one. Standard quoting (RFC 4180) handles it; doubling any quote inside
//     handles the rest.
//
//  ON THE SEPARATOR: comma, not semicolon. Their reporting chain is Google
//  Sheets and Looker Studio, both of which are unambiguous about commas. Excel
//  in a locale whose list separator is ';' will need Data → Text to Columns —
//  documented, rather than solved by a "sep=," line that Google Sheets would
//  then display as a stray first row.
// -----------------------------------------------------------------------------

/** Characters a spreadsheet treats as the start of a formula. */
const FORMULA_START = /^[=+\-@\t\r]/

/**
 * Format one value for a CSV cell.
 *
 * Types are handled separately on purpose: the formula guard must not be
 * applied to numbers, or every negative value in the file gains an apostrophe
 * and stops being a number.
 */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return ''

  if (typeof value === 'number') {
    // NaN and Infinity have no meaning in a spreadsheet cell; blank is honest.
    return Number.isFinite(value) ? String(value) : ''
  }

  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'

  if (value instanceof Date) return value.toISOString()

  let s = String(value)

  // The injection guard. Note it is applied BEFORE quoting, so the apostrophe
  // ends up inside the quotes where the spreadsheet will see it.
  if (FORMULA_START.test(s)) s = "'" + s

  if (/["\n\r,;]/.test(s) || s !== s.trim()) {
    s = '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

/**
 * Build a complete CSV document from an array of objects.
 *
 * `columns` is given explicitly rather than derived from the first row, because
 * deriving it means a row with a null in the first record silently drops a
 * column for the whole file.
 */
export function buildCsv(rows: Record<string, unknown>[], columns: string[]): string {
  const head = columns.map(csvCell).join(',')
  const body = rows.map((r) => columns.map((c) => csvCell(r[c])).join(','))
  // CRLF is what RFC 4180 says and what Excel is happiest with; every other
  // tool accepts it.
  return '﻿' + [head, ...body].join('\r\n') + '\r\n'
}

/**
 * Hand the file to the person.
 *
 * Kept separate from buildCsv so the building can be tested in Node, where
 * there is no Blob, no anchor element and no download folder. A function that
 * both builds and downloads is a function that cannot be tested.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Chrome on Android needs the URL to outlive the click by a moment.
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

/**
 * A filename someone can still identify in six months, in a Downloads folder
 * with forty other files in it: what it is, where from, and when.
 */
export function exportFilename(villageCode: string, when = new Date()): string {
  const d = when.toISOString().slice(0, 10)
  return `households-${villageCode}-${d}.csv`
}

// -----------------------------------------------------------------------------
//  THE COLUMNS, AND THEIR ORDER
// -----------------------------------------------------------------------------
//  Order matters more than it looks. Whoever opens this file reads left to
//  right and stops: identity, then where, then who lives there, then the
//  indicators, then the housekeeping. Coordinates sit near the end because
//  they are for a map, not for reading.
//
//  household_uid is FIRST and stays: it is the only column that can be used to
//  match this file against a later export without ambiguity. Anyone building a
//  Looker Studio join needs it.
// -----------------------------------------------------------------------------
export const EXPORT_COLUMNS = [
  'household_uid',
  'household_code',

  'province',
  'district',
  'commune',
  'village',
  'village_code',

  'head_name',
  'head_sex',
  'head_sex_label',

  'household_size',
  'adults_male_18_59',
  'adults_female_18_59',
  'children_under_18',
  'elderly_60_plus',
  'members_with_disability',

  'has_latrine',
  'latrine_type',
  'latrine_type_label',
  'latrine_improved',
  'latrine_functional',

  'water_source',
  'water_source_label',
  'water_improved',

  'handwashing_facility',
  'handwashing_label',
  'handwashing_jmp_service',

  'latitude',
  'longitude',
  'gps_accuracy_m',
  'gps_captured_at',

  'record_status',
  'last_assessed_at',
  'created_at',
  'updated_at',
  'registered_by',
  'open_quality_flags',
]
